import { useEffect, useState } from "react"
import { Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import moment from "moment"
import API from "@/common/API"
import Pagination from "@/components/core/Pagination"
import SuperAdminBackLink from "@/components/platform/SuperAdminBackLink"
import { parsedUser } from "@/common/GetCurrentUser"
import { isSuperAdmin } from "@/common/helpers"

interface VehicleStop {
  id: number
  externalVehicleId: number
  organizationId: string
  startDate: string
  endDate: string | null
  durationMinutes: number | null
  latitude: number | null
  longitude: number | null
  municipality: string | null
  province: string | null
  placeId: string | null
  placeMatchDistanceM: number | null
  matchStatus: string
  syncDate: string
}

interface Filters {
  organizationId: string
  dateFrom: string
  dateTo: string
  matchStatus: string
  externalVehicleId: string
}

interface OrganizationOption {
  id: string
  name: string
}

const MATCH_STATUS_OPTIONS = ["", "Matched", "Unmatched"]

interface VehicleStopsPageProps {
  embedded?: boolean
}

const VehicleStopsPage = ({ embedded = false }: VehicleStopsPageProps) => {
  const { t } = useTranslation("global")
  const user: any = parsedUser()
  const canSelectOrganization = isSuperAdmin(user)
  const userOrgId = user?.OrganizationId || user?.organizationId || ""

  const [records, setRecords] = useState<VehicleStop[]>([])
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  const QUANTITY = 50

  const [filters, setFilters] = useState<Filters>({
    organizationId: canSelectOrganization ? "" : userOrgId,
    dateFrom: "",
    dateTo: "",
    matchStatus: "",
    externalVehicleId: "",
  })

  const buildQuery = (page: number, f: Filters) => {
    const parts: string[] = [`Page=${page}`, `Quantity=${QUANTITY}`]
    if (f.organizationId) parts.push(`OrganizationId=${encodeURIComponent(f.organizationId)}`)
    if (f.dateFrom) parts.push(`DateFrom=${f.dateFrom}`)
    if (f.dateTo) parts.push(`DateTo=${f.dateTo}`)
    if (f.matchStatus) parts.push(`MatchStatus=${encodeURIComponent(f.matchStatus)}`)
    if (f.externalVehicleId) parts.push(`ExternalVehicleId=${f.externalVehicleId}`)
    return parts.join("&")
  }

  const loadData = async (page = 0, f = filters) => {
    try {
      setIsLoading(true)
      const res = await API.getAction("VehicleStops/List", buildQuery(page, f))
      if (res.data.status === "ok") {
        setRecords(res.data.response)
        setTotalPages(res.data.quantityOfPages)
        setTotalRecords(res.data.quantityOfRecords)
        setCurrentPage(page)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (!canSelectOrganization) return

    const loadOrganizations = async () => {
      try {
        setIsLoadingOrganizations(true)
        const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
        if (res.data.status === "ok") {
          setOrganizations(res.data.response || [])
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setIsLoadingOrganizations(false)
      }
    }

    loadOrganizations()
  }, [canSelectOrganization])

  useEffect(() => {
    loadData(0, filters)
  }, [filters.organizationId])

  const handleFilterChange = (field: keyof Filters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const handleSearch = () => loadData(0, filters)

  const handleClear = () => {
    const empty: Filters = {
      organizationId: canSelectOrganization ? "" : userOrgId,
      dateFrom: "",
      dateTo: "",
      matchStatus: "",
      externalVehicleId: "",
    }
    setFilters(empty)
    loadData(0, empty)
  }

  const matchBadge = (status: string) => {
    if (status === "Matched") return <Badge bg="success">{t("Matched")}</Badge>
    return <Badge bg="secondary">{t("Unmatched")}</Badge>
  }

  const content = (
    <>
      {!embedded && <SuperAdminBackLink />}

      {/* Header */}
      <div className="d-flex align-items-center mb-3 gap-2">
        <i className="fas fa-map-pin fa-lg text-primary" />
        <h4 className="mb-0 fw-bold">{t("Vehicle Stops")}</h4>
        {!isLoading && totalRecords > 0 && (
          <Badge bg="light" text="dark" className="ms-1">{totalRecords.toLocaleString()}</Badge>
        )}
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            {canSelectOrganization && (
              <Col xs={12} sm={6} md={3} lg={2}>
                <Form.Label className="small fw-semibold mb-1">{t("Organization")}</Form.Label>
                <Form.Select
                  size="sm"
                  value={filters.organizationId}
                  onChange={e => handleFilterChange("organizationId", e.target.value)}
                  disabled={isLoadingOrganizations}
                >
                  <option value="">{t("All Organizations")}</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>{org.name}</option>
                  ))}
                </Form.Select>
              </Col>
            )}
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label className="small fw-semibold mb-1">{t("Date From")}</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.dateFrom}
                onChange={e => handleFilterChange("dateFrom", e.target.value)}
              />
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label className="small fw-semibold mb-1">{t("Date To")}</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.dateTo}
                onChange={e => handleFilterChange("dateTo", e.target.value)}
              />
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label className="small fw-semibold mb-1">{t("Match Status")}</Form.Label>
              <Form.Select
                size="sm"
                value={filters.matchStatus}
                onChange={e => handleFilterChange("matchStatus", e.target.value)}
              >
                <option value="">{t("All")}</option>
                {MATCH_STATUS_OPTIONS.filter(Boolean).map(opt => (
                  <option key={opt} value={opt}>{t(opt)}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label className="small fw-semibold mb-1">{t("Vehicle ID")}</Form.Label>
              <Form.Control
                type="number"
                size="sm"
                placeholder={t("Vehicle ID")}
                value={filters.externalVehicleId}
                onChange={e => handleFilterChange("externalVehicleId", e.target.value)}
              />
            </Col>
            <Col xs={12} sm="auto">
              <div className="d-flex gap-2">
                <Button size="sm" variant="primary" onClick={handleSearch} disabled={isLoading}>
                  <i className="fa fa-search me-1" />
                  {t("Filter")}
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={handleClear} disabled={isLoading}>
                  {t("Clear")}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Results */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : records.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="fa fa-map-pin fa-2x mb-3 d-block" />
              {t("No stops found")}
            </div>
          ) : (
            <Table hover responsive className="mb-0 small align-middle">
              <thead className="table-light">
                <tr>
                  <th>{t("Start Date")}</th>
                  <th>{t("Vehicle ID")}</th>
                  <th>{t("Duration")}</th>
                  <th>{t("Location")}</th>
                  <th>{t("Place")}</th>
                  <th>{t("Distance")}</th>
                  <th>{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {records.map(stop => (
                  <tr key={stop.id}>
                    <td className="text-nowrap">
                      {moment(stop.startDate).format("MMM DD, YYYY HH:mm")}
                    </td>
                    <td>
                      <Badge bg="light" text="dark">#{stop.externalVehicleId}</Badge>
                    </td>
                    <td className="text-nowrap">
                      {stop.durationMinutes != null
                        ? `${stop.durationMinutes} ${t("min")}`
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      {[stop.municipality, stop.province].filter(Boolean).join(", ") || (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {stop.placeId ? (
                        <span className="text-success">
                          <i className="fa fa-check me-1" />
                          {stop.placeId.slice(0, 8)}…
                        </span>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {stop.placeMatchDistanceM != null
                        ? `${stop.placeMatchDistanceM} m`
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>{matchBadge(stop.matchStatus)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-3">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChanged={(page: number) => loadData(page, filters)}
            totalRecords={totalRecords}
          />
        </div>
      )}
    </>
  )

  if (embedded) {
    return <div>{content}</div>
  }

  return (
    <Container fluid className="px-4 py-3">
      {content}
    </Container>
  )
}

export default VehicleStopsPage
